import { useState, useRef } from 'react'
import { ReactComponent as IconClose } from 'bootstrap-icons/icons/x-lg.svg'
import { ReactComponent as IconLock } from 'bootstrap-icons/icons/lock.svg'

import Field from './Field'
import DateInput from './DateInput'
import { toISODate, toDisplayDate } from './date'
import { computeCalculated } from './calc'
import { useApi, paths } from './hooks'
import styles from './styles.module.scss'

const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0)

// Info fija de auditoría (a diferencia de "Fecha del control", que sí es editable).
// createdAt es epoch, y postgres.js lo devuelve como string — de ahí el Number(ts) || ts.
const createdInfo = (record) => {
  if (!record?.createdAt) return null
  const ts = Number(record.createdAt) || record.createdAt
  const author = record.createdByName || record.createdBy || record.createdByResourceID || '—'
  return `Creado el ${toDisplayDate(ts)} por ${author}`
}

// Como el resto de los registros de la HCE: editable solo por quien lo cargó y
// solo durante las 24hs posteriores a la carga. Un control nuevo (record null)
// siempre es editable. Sin excepciones desde el front: isStaff no bypasea ni la
// autoría ni la ventana de tiempo (a diferencia del backend, ver updateRecord.js).
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000
export const isEditable = (record, user, resource) => {
  if (!record) return true
  if (record.deleted) return false // eliminado: solo se abre para ver, nunca para editar
  const sameCreator = !!resource?.id && record.createdByResourceID === resource.id
  if (!sameCreator) return false
  const createdAt = record.createdAt != null ? Number(record.createdAt) : null
  return createdAt != null && (Date.now() - createdAt) <= EDIT_WINDOW_MS
}

// Modal para cargar / editar un control (fila fechada) de un formulario.
function CustomFormRecordEditor({ form, consumerID, record, resource, user, onClose, onSaved }) {
  const api = useApi()
  const columns = (form.columns || []).filter((c) => !c.hidden)
  const editable = isEditable(record, user, resource)
  const [data, setData] = useState(() => ({ ...(record?.data || {}) }))
  const [recordDate, setRecordDate] = useState(record?.recordDate || toISODate(Date.now()))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [invalidCols, setInvalidCols] = useState(() => new Map()) // colId -> mensaje de error
  const fieldRefs = useRef({})

  const setField = (colId, value) => {
    setData((prev) => ({ ...prev, [colId]: value }))
    if (invalidCols.has(colId)) {
      setInvalidCols((prev) => { const next = new Map(prev); next.delete(colId); return next })
    }
  }

  // Solo las preguntas obligatorias sin completar bloquean el guardado.
  // Un número fuera del rango min/max NO bloquea: se guarda igual y se destaca en
  // rojo con un aviso (los valores anormales suelen ser los clínicamente relevantes).
  const validate = () => {
    const errs = new Map()
    for (const col of columns) {
      if (col.type === 'calculated') continue
      if (col.required && isEmpty(data[col.id])) errs.set(col.id, 'Este campo es obligatorio.')
    }
    return errs
  }

  const handleSave = async () => {
    if (!editable) return
    const errs = validate()
    if (errs.size) {
      setInvalidCols(errs)
      fieldRefs.current[[...errs.keys()][0]]?.focus()
      return
    }
    setInvalidCols(new Map())
    setSaving(true)
    setError(null)
    try {
      const finalData = { ...data }
      for (const col of columns) {
        if (col.type === 'calculated') finalData[col.id] = computeCalculated(col, columns, data)
      }
      const saved = record
        ? await api(paths.updateRecord(form.id, record.id), { method: 'PATCH', body: { data: finalData, recordDate } })
        : await api(paths.createRecord(form.id), { method: 'POST', body: { consumerID, data: finalData, recordDate } })
      onSaved(saved)
    } catch (e) {
      setError(e.message)
      setSaving(false)
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div className={styles.title}>{record ? 'Editar control' : 'Nuevo control'} — {form.name}</div>
          <div className={styles.spacer} />
          <button type="button" className={styles.iconbtn} onClick={onClose} aria-label="Cerrar"><IconClose width="16" height="16" /></button>
        </div>

        <div className={styles.modalBody}>
          {!editable && (
            <div className={styles.note} style={{ marginBottom: '.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconLock width="12" height="12" />
              Los datos cargados ya no pueden ser modificados.
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Fecha del control</label>
            <DateInput
              style={{ maxWidth: 180 }}
              value={recordDate}
              disabled={!editable}
              onChange={(iso) => { if (iso) setRecordDate(iso) }}
            />
            {record && createdInfo(record) && <div className={styles.note} style={{ marginTop: 4 }}>{createdInfo(record)}</div>}
          </div>

          {columns.map((col) => (
            <Field
              key={col.id}
              column={col}
              columns={columns}
              data={data}
              value={data[col.id]}
              onChange={(v) => setField(col.id, v)}
              invalid={invalidCols.has(col.id)}
              invalidMsg={invalidCols.get(col.id)}
              fieldRef={(el) => { fieldRefs.current[col.id] = el }}
              disabled={!editable}
            />
          ))}

          {error && <div className="text-danger small mb-2">Error al guardar: {error}</div>}
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={`${styles.btn} ${styles.ghost}`} disabled={saving} onClick={onClose}>{editable ? 'Cancelar' : 'Cerrar'}</button>
          {editable && (
            <button type="button" className={`${styles.btn} ${styles.primary}`} disabled={saving} onClick={handleSave}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomFormRecordEditor
